import { StashCommonColor } from '@leaflink/stash';
import { SelectStatusOption } from '@leaflink/stash/SelectStatus.vue';

import { InternalCreditStatus } from '@/models/credit';
import { OrderStatusColorMap, OrderStatusRecord, ReplenishmentOrderStatusRecord } from '@/models/order';
import i18n from '@/plugins/i18n';
import { CreditStatus } from '@/services/api/models/credit';

/**
 * Activation Statuses
 */
export const ACTIVATION = Object.freeze({
  ACTIVE: {
    id: 'active',
    name: i18n.t('constants.activation_statuses.active'),
    color: 'teal-500',
  } as SelectStatusOption,
  INACTIVE: {
    id: 'inactive',
    name: i18n.t('constants.activation_statuses.inactive'),
    color: 'red-500',
  } as SelectStatusOption,
});

/**
 * Audit Log Statuses
 */
export const AUDIT_LOG = Object.freeze({
  CREATED: {
    icon: 'plus',
    name: i18n.t('common.added'),
  },
  EDITED: {
    icon: 'edit',
    name: i18n.t('common.edited'),
  },
  DELETED: {
    icon: 'trashcan',
    name: i18n.t('common.removed'),
  },
});

/**
 * Inventory Management Statuses
 * Note: for Buyer, Seller, and POS products
 */
export const INVENTORY_MANAGEMENT = {
  MANAGED: 1,
  UNLIMITED: 2,
  INHERITED: 3,
};

/**
 * Order Statuses taken from the OrderState model in the API
 * {@link https://github.com/LeafLink/leaflink/blob/b43b3888d36521fdb3375ea5a823118869700daf/apps/orders/choices.py#L12-L22|Permalink to the OrderState model in the API}
 */
export const ORDER_STATUSES = {
  ACCEPTED: 'accepted',
  BACKORDER: 'backorder',
  CANCELLED: 'cancelled',
  COMBINED: 'combined',
  COMPLETE: 'complete',
  DELIVERED: 'delivered',
  DRAFT: 'draft',
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected',
  SHIPPED: 'shipped',
  SUBMITTED: 'submitted',
} as const;

export const ORDER_STATUS_MAP: OrderStatusColorMap = {
  [ORDER_STATUSES.ACCEPTED]: {
    colorScheme: 'teal',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.accepted'),
  },
  [ORDER_STATUSES.BACKORDER]: {
    colorScheme: 'ice',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.backordered'),
  },
  [ORDER_STATUSES.CANCELLED]: {
    colorScheme: 'red',
    shade: 'light',
    label: i18n.t('constants.orderStatuses.cancelled'),
  },
  [ORDER_STATUSES.COMPLETE]: {
    colorScheme: 'green',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.complete'),
  },
  [ORDER_STATUSES.DELIVERED]: {
    colorScheme: 'seafoam',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.delivered'),
  },
  [ORDER_STATUSES.FULFILLED]: {
    colorScheme: 'royal',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.fulfilled'),
  },
  [ORDER_STATUSES.REJECTED]: {
    colorScheme: 'red',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.rejected'),
  },
  [ORDER_STATUSES.SHIPPED]: {
    colorScheme: 'blue',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.shipped'),
  },
  [ORDER_STATUSES.SUBMITTED]: {
    colorScheme: 'yellow',
    shade: 'main',
    label: i18n.t('constants.orderStatuses.submitted'),
  },
  [ORDER_STATUSES.COMBINED]: {
    colorScheme: 'ice',
    label: i18n.t('constants.orderStatuses.combined'),
    shade: 'main',
  },
} as const;

// The string[] type is intentionally used so that un-typed strings from the back-end (ex: order.status) can be matched against this list.
export const DIRECT_PAY_AUTO_AUTHORIZATION_STATUSES: { id: string; label: string }[] = [
  { id: 'backorder', label: 'Backorder' },
  { id: 'draft', label: 'Draft' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'fulfilled', label: 'Fulfilled' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'delivered', label: 'Delivered' },
] as const;

/**
 * Order statuses for both Buyer and Seller generated Orders.
 */
export const ORDER = Object.freeze<Omit<OrderStatusRecord, 'DELIVERED'>>({
  SUBMITTED: {
    name: 'submitted',
    color: 'ice-700',
    label: i18n.t('constants.orderStatuses.submitted'),
    transitionName: 'submit',
  },
  ACCEPTED: {
    name: 'accepted',
    color: 'teal-500',
    label: i18n.t('constants.orderStatuses.accepted'),
    transitionName: 'accept',
  },
  FULFILLED: {
    name: 'fulfilled',
    color: 'royal-500',
    label: i18n.t('constants.orderStatuses.fulfilled'),
    transitionName: 'fulfill',
  },
  SHIPPED: {
    name: 'shipped',
    color: 'blue-500',
    label: i18n.t('constants.orderStatuses.shipped'),
    transitionName: 'ship',
  },
  COMPLETE: {
    name: 'complete',
    color: 'purple-500',
    label: i18n.t('constants.orderStatuses.complete'),
    transitionName: 'complete',
  },
  REJECTED: {
    name: 'rejected',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.rejected'),
    transitionName: 'reject',
  },
  CANCELLED: {
    name: 'cancelled',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.cancelled'),
    transitionName: 'cancel',
  },
  DRAFT: {
    name: 'draft',
    color: 'orange-500',
    label: i18n.t('constants.orderStatuses.draft'),
    transitionName: '',
  },
  BACKORDER: {
    name: 'backorder',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.backorder'),
    transitionName: 'backorder',
  },
  COMBINED: {
    name: 'combined',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.combined'),
    transitionName: 'combine',
  },
});

/**
 * Order statuses for both Buyer and Seller generated Orders, with the new Delivered status
 */
export const ORDER_WITH_DELIVERED = Object.freeze<OrderStatusRecord>({
  SUBMITTED: {
    name: 'submitted',
    color: 'ice-700',
    label: i18n.t('constants.orderStatuses.submitted'),
    transitionName: 'submit',
  },
  ACCEPTED: {
    name: 'accepted',
    color: 'teal-500',
    label: i18n.t('constants.orderStatuses.accepted'),
    transitionName: 'accept',
  },
  FULFILLED: {
    name: 'fulfilled',
    color: 'royal-500',
    label: i18n.t('constants.orderStatuses.fulfilled'),
    transitionName: 'fulfill',
  },
  SHIPPED: {
    name: 'shipped',
    color: 'blue-500',
    label: i18n.t('constants.orderStatuses.shipped'),
    transitionName: 'ship',
  },
  DELIVERED: {
    name: 'delivered',
    color: 'green-500',
    label: i18n.t('constants.orderStatuses.delivered'),
    transitionName: 'deliver',
  },
  COMPLETE: {
    name: 'complete',
    color: 'purple-500',
    label: i18n.t('constants.orderStatuses.complete'),
    transitionName: 'complete',
  },
  REJECTED: {
    name: 'rejected',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.rejected'),
    transitionName: 'reject',
  },
  CANCELLED: {
    name: 'cancelled',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.cancelled'),
    transitionName: 'cancel',
  },
  DRAFT: {
    name: 'draft',
    color: 'orange-500',
    label: i18n.t('constants.orderStatuses.draft'),
    transitionName: '',
  },
  BACKORDER: {
    name: 'backorder',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.backorder'),
    transitionName: 'backorder',
  },
  COMBINED: {
    name: 'combined',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.combined'),
    transitionName: 'combine',
  },
});

/**
 * Assign order statuses for Seller generated Orders.
 */
export const ASSIGN_ORDER = Object.freeze<
  Omit<OrderStatusRecord, 'REJECTED' | 'CANCELLED' | 'COMBINED' | 'DRAFT' | 'DELIVERED'>
>({
  SUBMITTED: {
    name: 'submitted',
    color: 'ice-700',
    label: i18n.t('constants.orderStatuses.submitted'),
    transitionName: 'submit',
  },
  ACCEPTED: {
    name: 'accepted',
    color: 'teal-500',
    label: i18n.t('constants.orderStatuses.accepted'),
    transitionName: 'accept',
  },
  FULFILLED: {
    name: 'fulfilled',
    color: 'royal-500',
    label: i18n.t('constants.orderStatuses.fulfilled'),
    transitionName: 'fulfill',
  },
  SHIPPED: {
    name: 'shipped',
    color: 'blue-500',
    label: i18n.t('constants.orderStatuses.shipped'),
    transitionName: 'ship',
  },
  COMPLETE: {
    name: 'complete',
    color: 'purple-500',
    label: i18n.t('constants.orderStatuses.complete'),
    transitionName: 'complete',
  },
  BACKORDER: {
    name: 'backorder',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.backorder'),
    transitionName: 'backorder',
  },
});

/**
 * Statuses for replenishment orders
 */
export const REPLENISHMENT_ORDER_STATUSES = Object.freeze<ReplenishmentOrderStatusRecord>({
  DRAFT: {
    name: 'Draft',
    color: 'yellow-500',
    label: i18n.t('constants.orderStatuses.draft'),
  },
  PENDING: {
    name: 'Pending',
    color: 'yellow-500',
    label: i18n.t('constants.orderStatuses.pending'),
  },
  SUBMITTED: {
    name: 'Submitted',
    color: 'ice-700',
    label: i18n.t('constants.orderStatuses.submitted'),
  },
  FULFILLED: {
    name: 'Fulfilled',
    color: 'royal-500',
    label: i18n.t('constants.orderStatuses.fulfilled'),
  },
  SHIPPED: {
    name: 'Shipped',
    color: 'blue-500',
    label: i18n.t('constants.orderStatuses.shipped'),
  },
  CANCELLED: {
    name: 'Cancelled',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.cancelled'),
  },
  BACKORDER: {
    name: 'Backorder',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.backorder'),
  },
  UNKNOWN: {
    name: 'Unknown',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.unknown'),
    transitionName: '',
  },
  ERROR: {
    name: 'Error',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.error'),
    transitionName: '',
  },
});

/**
 * Statuses for replenishment admin orders
 */
export const REPLENISHMENT_ADMIN_ORDER_STATUSES = Object.freeze<Partial<ReplenishmentOrderStatusRecord>>({
  PENDING: {
    name: 'Pending',
    color: 'yellow-500',
    label: i18n.t('constants.orderStatuses.pending'),
  },
  SUBMITTED: {
    name: 'Submitted',
    color: 'ice-700',
    label: i18n.t('constants.orderStatuses.submitted'),
  },
  FULFILLED: {
    name: 'Fulfilled',
    color: 'royal-500',
    label: i18n.t('constants.orderStatuses.fulfilled'),
  },
  SHIPPED: {
    name: 'Shipped',
    color: 'blue-500',
    label: i18n.t('constants.orderStatuses.shipped'),
  },
  CANCELLED: {
    name: 'Cancelled',
    color: 'red-500',
    label: i18n.t('constants.orderStatuses.cancelled'),
  },
  BACKORDER: {
    name: 'Backorder',
    color: 'ice-500',
    label: i18n.t('constants.orderStatuses.backorder'),
  },
});

/**
 * Reverse Order transitions
 * TODO: update STATUSES.ORDER to have an index, and remove these
 */
export const REVERSE_TRANSITIONS = Object.freeze({
  [ORDER.COMPLETE.name]: [ORDER.SHIPPED.name, ORDER_WITH_DELIVERED.DELIVERED.name],
  [ORDER.FULFILLED.name]: [ORDER.ACCEPTED.name],
  [ORDER.SHIPPED.name]: [ORDER.FULFILLED.name],
});

/**
 * Local credit status for MOv3
 */
export const CREDIT_INTERNAL = Object.freeze({
  ...CreditStatus,
  ...InternalCreditStatus,
});

/**
 * Credit Statuses provided by the backend
 */
export const CREDIT = Object.freeze({
  ACTIVE: {
    id: CreditStatus.ACTIVE,
    name: i18n.t('constants.credit_statuses.active'),
    color: 'teal-500',
  },
  USED: {
    id: CreditStatus.USED,
    name: i18n.t('constants.credit_statuses.used'),
    color: 'royal-500',
  },
  CANCELLED: {
    id: CreditStatus.CANCELLED,
    name: i18n.t('constants.credit_statuses.cancelled'),
    color: 'red-500',
  },
});

/**
 * Certified Seller Statuses
 */
export const SELLER_STATUSES = Object.freeze({
  eliteSeller: {
    color: 'royal-500',
    id: 'elite',
  },
  powerSeller: {
    color: 'green-700',
    id: 'power',
  },
});

export const TRUSTED_SELLER = Object.freeze({
  ALERT: 'alert',
  FAIL: 'fail',
  SUCCESS: 'success',
});

/**
 * Possible Task statuses
 */
export const TASK = Object.freeze({
  TODO: {
    id: 'todo',
    color: 'blue-500',
    name: i18n.t('constants.taskStatuses.todo'),
  },
  IN_PROGRESS: {
    id: 'in_progress',
    color: 'orange-500',
    name: i18n.t('constants.taskStatuses.inProgress'),
  },
  DONE: {
    id: 'done',
    color: 'teal-500',
    name: i18n.t('constants.taskStatuses.done'),
  },
});

/**
 * Product listing state Statuses
 */
export const PRODUCT_LISTING_STATE = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  ARCHIVED: 'ARCHIVED',
  SAMPLE: 'SAMPLE',
  BACKORDER: 'BACKORDER',
  INTERNAL: 'INTERNAL',
  UNAVAILABLE: 'UNAVAILABLE',
});

/**
 * Product listing state colors
 */
export const PRODUCT_LISTING_STATE_COLORS = Object.freeze({
  [PRODUCT_LISTING_STATE.AVAILABLE]: {
    color: 'blue-500',
  },
  [PRODUCT_LISTING_STATE.ARCHIVED]: {
    color: 'purple-500',
  },
  [PRODUCT_LISTING_STATE.SAMPLE]: {
    color: 'teal-500',
  },
  [PRODUCT_LISTING_STATE.BACKORDER]: {
    color: 'ice-500',
  },
  [PRODUCT_LISTING_STATE.INTERNAL]: {
    color: 'ice-700',
  },
  [PRODUCT_LISTING_STATE.UNAVAILABLE]: {
    color: 'red-500',
  },
});

/**
 * Product unit denominations
 */
export const PRODUCT_UNIT_DENOMINATION = Object.freeze({
  ONE: {
    id: 1,
    label: '1',
    name: 'One',
    value: '1.00000',
  },
  HALF: {
    id: 2,
    label: '1/2',
    name: 'Half',
    value: '0.50000',
  },
  QUARTER: {
    id: 3,
    label: '1/4',
    name: 'Quarter',
    value: '0.25000',
  },
});

/**
 * Return Statuses
 */
export const RETURN = Object.freeze({
  SUBMITTED: {
    id: 'submitted',
    name: i18n.t('constants.returnStatuses.submitted'),
    color: 'teal-500', // select-status color
  },
  COMPLETE: {
    id: 'complete',
    name: i18n.t('constants.returnStatuses.complete'),
    color: 'royal-500', // select-status color
  },
  REJECTED: {
    id: 'rejected',
    name: i18n.t('constants.returnStatuses.rejected'),
    color: 'red-500', // select-status color
  },
});

/**
 * Logistics Order Statuses
 */
export const LOGISTICS_ORDER = Object.freeze({
  ASSIGNED: {
    id: 'assigned',
    name: i18n.t('constants.logisticsOrderStatuses.assigned'),
    color: 'ice-500', // select-status color
    shade: 'light',
  },
  DELIVERED: {
    id: 'delivered',
    name: i18n.t('constants.logisticsOrderStatuses.delivered'),
    color: 'green-500', // select-status color
    shade: 'main',
  },
  PLANNED: {
    id: 'planned',
    name: i18n.t('constants.logisticsOrderStatuses.planned'),
    color: 'blue-500', // select-status color
    shade: 'main',
  },
  SHIPPED: {
    id: 'shipped',
    name: i18n.t('constants.logisticsOrderStatuses.shipped'),
    color: 'purple-500', // select-status color
    shade: 'main',
  },
  UPLOADED: {
    id: 'uploaded',
    name: i18n.t('constants.logisticsOrderStatuses.uploaded'),
    color: 'teal-500', // select-status color
    shade: 'main',
  },
});

/**
 * Inbound Leads Statuses
 */

export const INBOUND_LEADS = Object.freeze({
  COMPLETE: {
    id: 'complete',
    label: i18n.t('constants.inboundLeadsStatuses.complete'),
    color: 'tw-bg-purple-500',
  },
  PENDING: {
    id: 'pending',
    label: i18n.t('constants.inboundLeadsStatuses.pending'),
    color: 'tw-bg-orange-500',
  },
  IGNORED: {
    id: 'ignored',
    label: i18n.t('constants.inboundLeadsStatuses.ignored'),
    color: 'tw-bg-red-500',
  },
});

/**
 * Types of orders discounts.
 */
export const ORDER_DISCOUNT_TYPES = {
  CUSTOMER: 'customer',
  PROMO_CODE: 'promo_code',
};

export const CAMPAIGN = Object.freeze({
  /** @type {'inactive'} */
  INACTIVE: 'inactive',
  /** @type {'live'} */
  LIVE: 'live',
  /** @type {'setup'} */
  SETUP: 'setup',
});

/**
 * Metrc Transfer Statuses
 */
export const METRC_TRANSFER = Object.freeze({
  NOT_STARTED: {
    id: 'not_started',
    name: i18n.t('constants.metrcTransferStatuses.notStarted.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.notStarted.tooltip'),
  },
  NOT_STARTED_PACKING_APP: {
    id: 'not_started',
    name: i18n.t('constants.metrcTransferStatuses.notStarted.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.notStarted.packingAppEnabledTooltip'),
  },
  NOT_STARTED_WMS: {
    id: 'not_started',
    name: i18n.t('constants.metrcTransferStatuses.notStarted.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.notStarted.wmsTooltip'),
  },
  PENDING: {
    id: 'pending',
    name: i18n.t('constants.metrcTransferStatuses.generating.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.generating.tooltip'),
  },
  PROCESSING: {
    id: 'pending',
    name: i18n.t('constants.metrcTransferStatuses.generating.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.generating.tooltip'),
  },
  SUCCESS: {
    id: 'teal',
    name: i18n.t('constants.metrcTransferStatuses.available.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.available.tooltip'),
  },
  FAILURE: {
    id: 'failure',
    name: i18n.t('constants.metrcTransferStatuses.error.name'),
    tooltip: i18n.t('constants.metrcTransferStatuses.error.tooltip'),
  },
});

/**
 * Pickup Task Statuses (tms_status)
 */
export const BRINGG_TASK_STATUSES = Object.freeze({
  PENDING: {
    id: 0,
    name: i18n.t('constants.bringgTaskStatuses.pending'),
    color: 'orange-500',
  },
  SUCCESS: {
    id: 1,
    name: i18n.t('constants.bringgTaskStatuses.success'),
    color: 'teal-500',
  },
  ERROR: {
    id: 2,
    name: i18n.t('constants.bringgTaskStatuses.failed'),
    color: 'red-500',
  },
});

/**
 * Required Action
 */

export const INVENTORY_TASKS_REQUIRED_ACTIONS = Object.freeze({
  LINK_CUSTOMER: 'link_customer',
  VERIFY_BUYER: 'verify_buyer',
});

export const INVENTORY_TASKS_REQUIRED_ACTIONS_OPTIONS = Object.freeze({
  LINK_CUSTOMER: {
    id: INVENTORY_TASKS_REQUIRED_ACTIONS.LINK_CUSTOMER,
    name: i18n.t('constants.requiredAction.linkCustomer'),
  },
  VERIFY_BUYER: {
    id: INVENTORY_TASKS_REQUIRED_ACTIONS.VERIFY_BUYER,
    name: i18n.t('constants.requiredAction.verifyBuyer'),
  },
});

/**
 * Invoice statuses
 */
export const PAYMENT_STATUS = Object.freeze({
  PARTIALLY_PAID: {
    id: 'partially paid',
    status: i18n.t('common.partiallyPaid'),
    button: i18n.t('constants.invoiceStatus.makePayment'),
    colorScheme: 'orange', // chip color scheme
  },
  PAID: {
    id: 'paid',
    status: i18n.t('common.paid'),
    button: i18n.t('common.paid'),
    colorScheme: 'green', // chip color scheme
  },
  UNPAID: {
    id: 'unpaid',
    status: i18n.t('common.unpaid'),
    button: i18n.t('constants.invoiceStatus.makePayment'),
    colorScheme: 'orange', // chip color scheme
  },
  PENDING: {
    id: 'pending',
    status: i18n.t('common.pending'),
    button: i18n.t('common.pending'),
    colorScheme: 'yellow', // chip color scheme
  },
  OVERDUE: {
    id: 'overdue',
    status: i18n.t('common.overdue'),
    button: i18n.t('constants.invoiceStatus.makePayment'),
    colorScheme: 'red', // chip color scheme
  },
});

/**
 * Buyer verified/unverified Statuses
 */
export const BUYER_VERIFICATION_STATUS = {
  UNVERIFIED: {
    id: 'false',
    displayName: i18n.t('constants.buyerVerificationStatuses.unverified'),
  },
  VERIFIED: {
    id: 'true',
    displayName: i18n.t('constants.buyerVerificationStatuses.verified'),
  },
};

/**
 * Volume Discount Statuses
 */
export const VOLUME_DISCOUNTS = Object.freeze({
  ACTIVE: {
    id: 'active',
    name: i18n.t('constants.activation_statuses.active'),
    color: 'teal-500',
  },
  INACTIVE: {
    id: 'inactive',
    name: i18n.t('constants.activation_statuses.inactive'),
    color: 'red-500',
  },
});

/**
 * Backfill Job Statuses
 */
export const BACKFILL_JOB_STATUS = Object.freeze({
  PENDING: {
    id: 0,
    name: i18n.t('constants.backfillJobStatuses.pending'),
    color: 'tw-text-orange-500',
  },
  SUCCESS: {
    id: 1,
    name: i18n.t('constants.backfillJobStatuses.success'),
    color: 'tw-text-teal-500',
  },
  FAILED: {
    id: 2,
    name: i18n.t('constants.backfillJobStatuses.failed'),
    color: 'tw-text-red-500',
  },
  CANCELLED: {
    id: 3,
    name: i18n.t('constants.backfillJobStatuses.cancelled'),
    color: 'tw-text-red-500',
  },
});

/**
 * Status of a company's invitation to Join Payments (seller<>buyer)
 */

export const PAYMENTS_INVITATION_STATUS = {
  ACCEPTED: 'accepted',
  IGNORED: 'ignored',
  SENT: 'sent',
};

export const REQUEST_STATUS = {
  FAIL: 'fail',
  PENDING: 'pending',
  SUCCESS: 'success',
};

type OrderStatus = 'accepted' | 'submitted' | 'shipped' | 'fulfilled' | 'rejected' | 'backordered' | 'canceled';

interface OrderStatusAttrs {
  label: string;
  color: string;
  shade: string;
  buttons: string[];
}

export const BUYER_ORDERS_STATUSES = Object.freeze<Record<Uppercase<OrderStatus>, OrderStatusAttrs>>({
  ACCEPTED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.accepted'),
    color: 'teal',
    shade: 'main',
    buttons: ['message'],
  },
  SUBMITTED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.submitted'),
    color: 'yellow',
    shade: 'main',
    buttons: ['message', 'viewAccept'],
  },
  SHIPPED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.shipped'),
    color: 'blue',
    shade: 'main',
    buttons: ['message', 'markReceived'],
  },
  FULFILLED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.fulfilled'),
    color: 'royal',
    shade: 'main',
    buttons: ['message', 'reorder'],
  },
  REJECTED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.rejected'),
    color: 'red',
    shade: 'main',
    buttons: ['message'],
  },
  BACKORDERED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.backordered'),
    color: 'ice',
    shade: 'main',
    buttons: ['cancel', 'message'],
  },
  CANCELED: {
    label: i18n.t('constants.buyerDashboardOMSStatuses.canceled'),
    color: 'red',
    shade: 'light',
    buttons: ['message'],
  },
});

interface TypeInfo {
  label: string;
  bgColor: StashCommonColor;
  textColor: string;
}

interface OrderTypes {
  BGO: TypeInfo;
  SGO: TypeInfo;
}

export const BUYER_ORDERS_TYPES = Object.freeze<OrderTypes>({
  BGO: {
    label: 'BGO',
    bgColor: 'ice-200',
    textColor: 'ice-900',
  },
  SGO: {
    label: 'SGO',
    bgColor: 'ice-700',
    textColor: 'white',
  },
});
